'use client';

import { useState, useEffect } from 'react';
import ShareButtons from '@/app/components/ShareButtons';

interface Props {
  id1: string;
  id2: string;
  slugA: string;
  slugB: string;
  initialLeft: number;
  initialRight: number;
  initialVsSendA: number;
  initialVsSendB: number;
}

export default function VsTally({ id1, id2, slugA, slugB, initialLeft, initialRight, initialVsSendA, initialVsSendB }: Props) {
  const [leftCount, setLeftCount] = useState(initialLeft);
  const [rightCount, setRightCount] = useState(initialRight);
  const [vsSendA, setVsSendA] = useState(initialVsSendA);
  const [vsSendB, setVsSendB] = useState(initialVsSendB);
  const [url, setUrl] = useState('');

  useEffect(() => {
    setUrl(window.location.href);
    // Fire-and-forget view count
    fetch('/api/vs-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slugA, slugB }),
    }).catch(() => {});
    if (typeof window.umami !== 'undefined') window.umami.track('vs_battle_viewed');
  }, [slugA, slugB]);

  useEffect(() => {
    const poll = async () => {
      try {
        const [l, r, vs] = await Promise.all([
          fetch(`/api/messages/${id1}`).then(r => r.json()),
          fetch(`/api/messages/${id2}`).then(r => r.json()),
          fetch(`/api/vs-view?slugA=${encodeURIComponent(slugA)}&slugB=${encodeURIComponent(slugB)}`).then(r => r.json()),
        ]);
        if (typeof l.send_count === 'number') setLeftCount(l.send_count);
        if (typeof r.send_count === 'number') setRightCount(r.send_count);
        if (typeof vs.send_count_a === 'number') setVsSendA(vs.send_count_a);
        if (typeof vs.send_count_b === 'number') setVsSendB(vs.send_count_b);
      } catch {
        // Silently ignore poll errors
      }
    };

    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [id1, id2, slugA, slugB]);

  const total = leftCount + rightCount;
  const leftPct = total > 0 ? Math.round((leftCount / total) * 100) : 50;
  const rightPct = 100 - leftPct;

  return (
    <div className="flex flex-col gap-4">
      {/* Tally bar */}
      <div>
        <div className="flex justify-between text-xs font-bold mb-1.5">
          <span className="text-blue-400">{leftPct}% · {leftCount.toLocaleString()} sent</span>
          <span className="text-red-400">{rightCount.toLocaleString()} sent · {rightPct}%</span>
        </div>
        <div className="h-4 rounded-full overflow-hidden flex bg-[#1e3050]">
          <div className="bg-blue-500 transition-all duration-700" style={{ width: `${leftPct}%` }} />
          <div className="bg-red-500 transition-all duration-700" style={{ width: `${rightPct}%` }} />
        </div>
        {total > 0 && (
          <p className="text-center text-xs text-slate-500 mt-1.5">
            {total.toLocaleString()} total postcards sent on this issue
            {(vsSendA + vsSendB) > 0 && (
              <> · <span className="text-amber-500/80">{(vsSendA + vsSendB).toLocaleString()} from this page</span></>
            )}
          </p>
        )}
      </div>

      {/* Share block */}
      {url && (
        <div className="bg-[#0a1628] border border-amber-500/20 rounded-xl px-4 py-4">
          <p className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-3">Share This Debate</p>
          <ShareButtons
            url={url}
            text="Two sides. One issue. Where do you stand? Send a postcard to Congress in under a minute:"
            className="mb-3"
          />
          <p className="text-xs text-slate-500 font-mono break-all mt-2">{url}</p>
        </div>
      )}
    </div>
  );
}
